import { MapPin, Phone, Mail, Clock, ChevronDown } from "lucide-react";
import React, { useState, FormEvent, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

const SHOW_FORM = true;

// ---------------------- TYPES ----------------------

interface AccordionItem {
  q: string;
  a: string;
}

interface ContactRowProps {
  icon: React.ReactNode;
  title: string;
  value: string;
}

interface InputProps {
  label: string;
  placeholder: string;
  name: string;
  required?: boolean;
}

interface TextareaProps {
  label: string;
  placeholder: string;
  name: string;
  required?: boolean;
}

interface AccordionProps {
  item: AccordionItem;
  open: boolean;
  toggle: () => void;
}

interface AccordionGroupProps {
  items: AccordionItem[];
}

// ---------------------- MAIN PAGE ----------------------

export default function ContactPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <main className="min-h-screen bg-background text-text DEFAULT">
      {/* HERO */}
      <section className="py-20 bg-background border-border">
        <div className="container-custom max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-heading italic text-primary DEFAULT mb-4">
            Get in Touch
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Whether you’re looking for help, suggestions, or just want to say hello —
            we’re here for you.
          </p>
        </div>
      </section>

      {/* CONTACT INFO + FORM */}
      <section className="py-24">
        <div className="container-custom max-w-6xl mx-auto px-4 grid lg:grid-cols-2 gap-16">

          {/* LEFT — CONTACT DETAILS */}
          <div>
            <h2 className="text-3xl font-semibold text-primary DEFAULT mb-6">
              Contact Information
            </h2>

            <p className="text-text-secondary mb-10 leading-relaxed">
              Reach out anytime. Our team usually responds within a few hours.
            </p>

            <div className="space-y-6">
              <ContactRow
                icon={<MapPin className="w-6 h-6 text-primary-600" />}
                title="Location"
                value="Sunrise Bookstore, 8V9X+GCX, Court Rd, Swami Vivekananda Nagar, Suratgarh, Rajasthan 335804"
              />

              <ContactRow
                icon={<Phone className="w-6 h-6 text-primary-600" />}
                title="Phone"
                value="+91-9460999000"
              />

              <ContactRow
                icon={<Mail className="w-6 h-6 text-primary-600" />}
                title="Email"
                value="sunrisephotostate@gmail.com"
              />

              <ContactRow
                icon={<Clock className="w-6 h-6 text-primary-600" />}
                title="Working Hours"
                value="Mon - Sun : 09:00 AM - 08:00 PM"
              />
            </div>

            {/* MAP */}
            <div className="mt-12 rounded-2xl overflow-hidden border border-border shadow-sm">
              <iframe
                title="Sunrise Store Map"
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                src="https://maps.google.com/maps?width=600&height=400&hl=en&q=8V9X%2BGCX%2C%20Court%20Rd%2C%20Swami%20Vivekananda%20Nagar%2C%20Suratgarh%2C%20Rajasthan%20335804&t=&z=14&ie=UTF8&iwloc=B&output=embed"
              ></iframe>
            </div>
          </div>

          {/* RIGHT — CONTACT FORM */}
          {SHOW_FORM && <FormCard />}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background-muted border-t border-border">
        <div className="container-custom max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-heading italic text-primary DEFAULT mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto mb-12">
            Find answers to common questions about orders, delivery, and more.
          </p>

          <AccordionGroup
            items={[
              { q: "How long does delivery take?", a: "Books are typically delivered within 2–4 business days." },
              { q: "Can I return a book?", a: "Yes, books can be returned within 7 days in original condition." },
              { q: "Do you ship internationally?", a: "Currently we ship only within India." },
              { q: "How can I check my order status?", a: "Visit the Orders page after logging into your account." }
            ]}
          />
        </div>
      </section>
    </main>
  );
}

/* ---------------------- CONTACT ROW ---------------------- */

function ContactRow({ icon, title, value }: ContactRowProps) {
  return (
    <div className="flex gap-4 items-start">
      <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
        {icon}
      </div>

      <div>
        <p className="text-sm text-text-secondary">{title}</p>
        <p className="text-text DEFAULT font-medium">{value}</p>
      </div>
    </div>
  );
}

/* ---------------------- FORM WITH WEB3FORMS ---------------------- */


import { toast } from "sonner";

function FormCard() {
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const phone = formData.get('phone') as string;
    const subject = formData.get('subject') as string;
    const message = formData.get('message') as string;

    try {
      const functions = getFunctions(undefined, 'asia-south1');
      const sendContactForm = httpsCallable(functions, 'sendContactFormEmail');
      
      const result = await sendContactForm({
        name,
        email,
        phone,
        subject,
        message,
      });

      toast.success("Message sent successfully. We'll get back to you soon!");
      form.reset();
    } catch (error: any) {
      console.error('Contact form error:', error);
      toast.error(error.message || "Failed to send message. Please try again.");
    }

    setLoading(false);
  }

  return (
    <div className="bg-background-soft border border-border rounded-3xl p-10 shadow-md">
      <h3 className="text-3xl font-semibold mb-6 text-text DEFAULT">
        Write to Us
      </h3>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <Input name="name" label="Name" placeholder="Your name" required />
        <Input name="email" label="Email" placeholder="Your email" required />
        <Input name="phone" label="Phone (Optional)" placeholder="+91 9876543210" />
        <Input name="subject" label="Subject" placeholder="Order inquiry, Support request, etc." required />
        <Textarea name="message" label="Message" placeholder="Write your message..." required />

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl bg-primary-600 hover:bg-primary-dark text-white font-medium transition disabled:opacity-60"
        >
          {loading ? "Sending..." : "Send Message"}
        </button>
      </form>
    </div>
  );
}


/* ---------------------- INPUTS ---------------------- */

function Input({ label, placeholder, name, required }: InputProps) {
  return (
    <div className="space-y-2">
      <label className="text-sm text-text-secondary">{label}</label>
      <input
        name={name}
        required={required}
        className="w-full h-12 px-4 rounded-xl border border-border bg-background focus:ring-2 focus:ring-primary-light outline-none"
        placeholder={placeholder}
      />
    </div>
  );
}

function Textarea({ label, placeholder, name, required }: TextareaProps) {
  return (
    <div className="space-y-2">
      <label className="text-sm text-text-secondary">{label}</label>
      <textarea
        name={name}
        required={required}
        rows={5}
        className="w-full px-4 py-3 rounded-xl border border-border bg-background focus:ring-2 focus:ring-primary-light outline-none resize-none"
        placeholder={placeholder}
      />
    </div>
  );
}

/* ---------------------- ACCORDION ---------------------- */

function AccordionGroup({ items }: AccordionGroupProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-4 text-left max-w-3xl mx-auto">
      {items.map((item, index) => (
        <Accordion
          key={index}
          item={item}
          open={openIndex === index}
          toggle={() => setOpenIndex(openIndex === index ? null : index)}
        />
      ))}
    </div>
  );
}

function Accordion({ item, open, toggle }: AccordionProps) {
  return (
    <div
      className={`rounded-2xl border ${
        open ? "bg-background-muted" : "bg-background"
      } border-border p-6 shadow-sm transition`}
    >
      <button className="w-full flex justify-between items-center text-left" onClick={toggle}>
        <span className="text-lg font-medium text-primary DEFAULT">
          {item.q}
        </span>

        <ChevronDown
          className={`w-6 h-6 text-primary-600 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <p className="text-text-secondary mt-4 leading-relaxed">{item.a}</p>
      )}
    </div>
  );
}
